"use client";

import type React from "react";
import {
  List,
  Typography,
  Space,
  Avatar,
  Tag,
  Button,
  Progress,
  Empty,
  Divider,
  Card,
  Statistic,
  Popconfirm,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FolderOutlined,
  FileOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { useState, useEffect } from "react";
import {
  MusicObject,
  StoredMusicFile,
  useDownloadMusic,
} from "../../context/DownloadAudioProvider";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { useAudio } from "../../context/AudioProvider";
import { useNavigate } from "react-router";
import { DownloadProgressMonitor } from "./AudioDownloadProgress";

const { Title, Text } = Typography;

export const DownloadHistoryList: React.FC = () => {
  const {
    downloadStates,
    downloadHistory,
    currentlyDownloading,
    removeFromHistory,
    downloadMusic,
    getStorageInfo,
  } = useDownloadMusic();

  const [storageInfo, setStorageInfo] = useState({
    totalFiles: 0,
    totalSize: 0,
  });

  const { setMusicList, setCurrentPlayerUrl } = useAudio();
  const navigate = useNavigate();

  useEffect(() => {
    updateStorageInfo();
  }, [downloadHistory]);

  const updateStorageInfo = async () => {
    try {
      const info = await getStorageInfo();
      setStorageInfo(info);
    } catch (error) {
      console.error("Failed to get storage info:", error);
    }
  };

  // Get currently downloading items
  const currentDownloads = Object.values(currentlyDownloading).filter(
    (music) => downloadStates[music.id]?.isDownloading
  );

  // Get completed downloads
  const completedDownloads = downloadHistory.filter(
    (file) => downloadStates[file.id]?.isCompleted || !downloadStates[file.id]
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDelete = async (musicId: string) => {
    try {
      await removeFromHistory(musicId);
    } catch (error) {
      console.error("Failed to delete file:", error);
    }
  };

  const handlePlay = async (filePath: string, musicObj: MusicObject) => {
    try {
      //   console.log(filePath);
      const mediaContent = await Filesystem.readFile({
        path: `${filePath}`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });

      //   console.log(mediaContent.data);

      setMusicList([
        {
          id: musicObj?.id + "",
          // musicFile: book.mainAudio?.fileUrl || "",
          musicFile: `data:audio/mpeg;base64,${mediaContent?.data}`,
          title: musicObj?.title || "",
          coverImage: musicObj?.coverImage || "",
          duration: musicObj.duration,
          artist: musicObj.artist,
          description: musicObj.description,
        },
      ]);

      setCurrentPlayerUrl(
        `/users/book-details-audio/${musicObj.id}/${encodeURIComponent(
          musicObj.title.replace(/\s+/g, "_")
        )}`
      );
      navigate(
        `/users/book-details-audio/${musicObj.id}/${encodeURIComponent(
          musicObj.title.replace(/\s+/g, "_")
        )}`
      );
    } catch (error) {
      console.error("Failed to Load Music:", error);
    }
  };

  const renderDownloadItem = (
    item: Partial<StoredMusicFile & MusicObject>,
    showProgress = false
  ) => {
    const downloadState = downloadStates[item.id || 0];
    const music = item.music || item; // Handle both StoredMusicFile and MusicObject
    const isStoredFile = !!item.music; // Check if it's a StoredMusicFile
    const filePath = isStoredFile && item.filePath;

    return (
      <List.Item key={item.id} className="flex flex-col">
        <div className="flex w-full flex-col p-2">
          {/* Avatar + Title */}
          <div className="flex items-center space-x-3 mb-2">
            <Avatar
              style={{ width: "48px", height: "48px" }}
              src={music.coverImage || "/placeholder.svg?height=64&width=64"}
            />
            <Text strong className="text-sm  text-gray-900 dark:text-stone-100">
              {music.title}
            </Text>
          </div>

          {/* Meta info */}
          <div className="flex flex-col space-y-1 text-xs  text-gray-900 dark:text-stone-100">
            <Text
              type="secondary"
              className="text-sm  text-gray-900 dark:text-stone-100"
            >
              {music.artist} — {music.duration}
            </Text>

            <div className="flex flex-wrap gap-x-3">
              {isStoredFile && item.fileSize && (
                <span>
                  <FileOutlined /> {formatFileSize(item.fileSize)}
                </span>
              )}
              {isStoredFile && item.downloadDate && (
                <span>Downloaded: {formatDate(item.downloadDate)}</span>
              )}

              {downloadState?.isDownloading ? (
                <Tag color="processing" icon={<ClockCircleOutlined />}>
                  {downloadState.progress}%
                </Tag>
              ) : isStoredFile ? (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  Local
                </Tag>
              ) : (
                <Tag color="default">Ready</Tag>
              )}
            </div>

            {showProgress && downloadState?.isDownloading && (
              <div className="mt-1">
                <Progress
                  percent={downloadState.progress}
                  size="small"
                  status="active"
                  strokeColor={{
                    "0%": "#108ee9",
                    "100%": "#87d068",
                  }}
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-row space-x-3 mt-4 w-full">
            <Button
              block
              type="primary"
              icon={<PlayCircleOutlined />}
              className="mb-2 sm:mb-0 text-white bg-blue-600 hover:bg-blue-700"
              onClick={() => handlePlay(filePath || "", music as MusicObject)}
              disabled={downloadState?.isDownloading}
            >
              Play
            </Button>

            <Popconfirm
              title="Are you sure you want to delete this file?"
              onConfirm={() => handleDelete(item.id as string)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                block
                icon={<DeleteOutlined />}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        </div>
      </List.Item>
    );
  };

  return (
    <div className="w-full p-4 min-h-screen bg-gray-50 dark:bg-stone-900">
      {/* Add this new component */}
      <DownloadProgressMonitor />

      {/* Storage Statistics */}
      {/* AntD Card will be themed by AntD. We ensure text within is adaptable if not directly part of AntD components. */}
      <Card className="mb-6 dark:bg-stone-800 dark:border-stone-700">
        <div className="grid grid-cols-2 gap-4">
          <Statistic
            title={
              <span className="text-gray-900 dark:text-stone-100">
                {"Total Files"}
              </span>
            }
            value={storageInfo.totalFiles}
            prefix={<FolderOutlined />}
            valueStyle={{ color: "#1890ff" }}
          />

          <Statistic
            title={
              <span className="text-gray-900 dark:text-stone-100">
                {"Storage Used"}
              </span>
            }
            value={formatFileSize(storageInfo.totalSize)}
            prefix={<FileOutlined />}
            valueStyle={{ color: "#52c41a" }}
          />
        </div>
      </Card>

      <div className="mb-6">
        <Title level={4} className="mb-2 text-gray-900 dark:text-stone-100">
          Currently Downloading
        </Title>
        {currentDownloads.length > 0 ? (
          <List
            className="bg-white dark:bg-stone-800 rounded-lg shadow"
            itemLayout="horizontal"
            dataSource={currentDownloads}
            renderItem={(item) => renderDownloadItem(item, true)}
          />
        ) : (
          <Empty
            description={
              <span className="text-gray-900 dark:text-stone-100">
                {"No active downloads"}
              </span>
            }
            className="bg-white dark:bg-stone-800 p-6 rounded-lg shadow"
          />
        )}
      </div>

      <Divider className="dark:border-stone-700" />

      <div>
        <Title level={4} className="mb-2 text-gray-900 dark:text-stone-100">
          Stored Audio Files
        </Title>
        {completedDownloads.length > 0 ? (
          <List
            className="bg-white dark:bg-stone-800 rounded-lg shadow"
            itemLayout="horizontal"
            dataSource={completedDownloads}
            renderItem={(item) => renderDownloadItem(item)}
          />
        ) : (
          <Empty
            description={
              <span className="text-gray-900 dark:text-stone-100">
                {"No stored music files"}
              </span>
            }
            className="bg-white dark:bg-stone-800 p-6 rounded-lg shadow"
          />
        )}
      </div>
    </div>
  );
};
